import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, RefreshCw, User, Briefcase, Car, Phone, Mail, MapPin,
  Calendar, CheckCircle, Clock, MessageSquare, ChevronDown, ChevronUp,
  FileText, Loader2, Search, Filter
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  reviewed: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  contacted: 'bg-green-500/20 text-green-400 border-green-500/30'
};

const statusLabels = {
  new: 'New',
  reviewed: 'Reviewed',
  contacted: 'Contacted'
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
  });

  const fetchApplications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/applications`, getAuthHeader());
      setApplications(response.data);
      setFilteredApps(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        toast.error('Failed to fetch applications');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchApplications();
  }, [navigate, fetchApplications]);

  useEffect(() => {
    let filtered = applications;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app =>
        app.firstName.toLowerCase().includes(term) ||
        app.lastName.toLowerCase().includes(term) ||
        app.email.toLowerCase().includes(term) ||
        app.phone.includes(term) ||
        `${app.vehicleYear} ${app.vehicleMake} ${app.vehicleModel}`.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    setFilteredApps(filtered);
  }, [searchTerm, statusFilter, applications]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const handleStatusUpdate = async (appId, newStatus) => {
    setUpdatingId(appId);
    try {
      await axios.patch(
        `${API}/admin/applications/${appId}`,
        { status: newStatus },
        getAuthHeader()
      );
      setApplications(prev =>
        prev.map(app => app.id === appId ? { ...app, status: newStatus } : app)
      );
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    return `$${Number(value).toLocaleString()}`;
  };

  const maskSSN = (ssn) => {
    if (!ssn) return 'N/A';
    return `***-**-${ssn.slice(-4)}`;
  };

  const stats = {
    total: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    contacted: applications.filter(a => a.status === 'contacted').length
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={companyInfo.logo} alt={companyInfo.name} className="h-10 w-auto" />
              <span className="text-white font-semibold">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchApplications}
                className="bg-transparent border-zinc-700 text-gray-300 hover:bg-zinc-800"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="bg-transparent border-zinc-700 text-gray-300 hover:bg-zinc-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
            <div className="text-gray-400 text-sm">Total Applications</div>
            <div className="text-2xl font-bold text-white mt-1">{stats.total}</div>
          </div>
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
            <div className="text-blue-400 text-sm">New</div>
            <div className="text-2xl font-bold text-white mt-1">{stats.new}</div>
          </div>
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
            <div className="text-yellow-400 text-sm">Reviewed</div>
            <div className="text-2xl font-bold text-white mt-1">{stats.reviewed}</div>
          </div>
          <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4">
            <div className="text-green-400 text-sm">Contacted</div>
            <div className="text-2xl font-bold text-white mt-1">{stats.contacted}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="text"
              placeholder="Search by name, email, phone, or vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-500" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-zinc-900 border-zinc-700 text-white">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700">
                <SelectItem value="all" className="text-white hover:bg-zinc-800">All Status</SelectItem>
                <SelectItem value="new" className="text-white hover:bg-zinc-800">New</SelectItem>
                <SelectItem value="reviewed" className="text-white hover:bg-zinc-800">Reviewed</SelectItem>
                <SelectItem value="contacted" className="text-white hover:bg-zinc-800">Contacted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Applications List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No applications found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApps.map((app) => (
              <div
                key={app.id}
                className="bg-zinc-800/50 border border-zinc-700 rounded-xl overflow-hidden"
              >
                {/* Collapsed Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-zinc-800/80 transition-colors"
                  onClick={() => setExpandedId(expandedId === app.id ? null : app.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-teal-500/20 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">
                          {app.firstName} {app.lastName}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {app.vehicleYear} {app.vehicleMake} {app.vehicleModel}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[app.status]}`}>
                        {statusLabels[app.status]}
                      </span>
                      <span className="text-gray-500 text-sm hidden sm:block">
                        {formatDate(app.createdAt)}
                      </span>
                      {expandedId === app.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === app.id && (
                  <div className="border-t border-zinc-700 p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Personal Info */}
                      <div className="space-y-4">
                        <h4 className="text-teal-400 font-medium flex items-center gap-2">
                          <User className="w-4 h-4" /> Personal Information
                        </h4>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center gap-2 text-gray-300">
                            <Mail className="w-4 h-4 text-gray-500" />
                            <a href={`mailto:${app.email}`} className="hover:text-teal-400">{app.email}</a>
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <Phone className="w-4 h-4 text-gray-500" />
                            <a href={`tel:${app.phone}`} className="hover:text-teal-400">{app.phone}</a>
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            DOB: {app.dateOfBirth}
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <FileText className="w-4 h-4 text-gray-500" />
                            SSN: {maskSSN(app.ssn)}
                          </div>
                          <div className="flex items-start gap-2 text-gray-300">
                            <MapPin className="w-4 h-4 text-gray-500 mt-0.5" />
                            <span>
                              {app.streetAddress}<br />
                              {app.city}, {app.state} {app.zipCode}
                            </span>
                          </div>
                          <div className="text-gray-400">
                            Housing: {app.housingType}
                            {app.monthlyPayment && ` • ${formatCurrency(app.monthlyPayment)}/mo`}
                          </div>
                        </div>
                      </div>

                      {/* Employment Info */}
                      <div className="space-y-4">
                        <h4 className="text-teal-400 font-medium flex items-center gap-2">
                          <Briefcase className="w-4 h-4" /> Employment
                        </h4>
                        <div className="space-y-3 text-sm">
                          <div className="text-gray-300">
                            <span className="text-gray-500">Status:</span> {app.employmentType}
                          </div>
                          {app.employerName && (
                            <div className="text-gray-300">
                              <span className="text-gray-500">Employer:</span> {app.employerName}
                            </div>
                          )}
                          {app.jobTitle && (
                            <div className="text-gray-300">
                              <span className="text-gray-500">Title:</span> {app.jobTitle}
                            </div>
                          )}
                          {app.yearsEmployed && (
                            <div className="text-gray-300">
                              <span className="text-gray-500">Years:</span> {app.yearsEmployed}
                            </div>
                          )}
                          <div className="text-gray-300">
                            <span className="text-gray-500">Monthly Income:</span> {formatCurrency(app.monthlyIncome)}
                          </div>
                        </div>
                      </div>

                      {/* Vehicle Info */}
                      <div className="space-y-4">
                        <h4 className="text-teal-400 font-medium flex items-center gap-2">
                          <Car className="w-4 h-4" /> Vehicle
                        </h4>
                        <div className="space-y-3 text-sm">
                          <div className="text-gray-300">
                            <span className="text-gray-500">Vehicle:</span> {app.vehicleYear} {app.vehicleMake} {app.vehicleModel}
                          </div>
                          {app.vinNumber && (
                            <div className="text-gray-300">
                              <span className="text-gray-500">VIN:</span> {app.vinNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Status Actions */}
                    <div className="mt-6 pt-6 border-t border-zinc-700">
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="text-gray-400 text-sm">Update Status:</span>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={app.status === 'new' ? 'default' : 'outline'}
                            onClick={() => handleStatusUpdate(app.id, 'new')}
                            disabled={updatingId === app.id}
                            className={app.status === 'new'
                              ? 'bg-blue-500 hover:bg-blue-600 text-white'
                              : 'bg-transparent border-zinc-600 text-gray-300 hover:bg-zinc-800'
                            }
                          >
                            <Clock className="w-4 h-4 mr-1" />
                            New
                          </Button>
                          <Button
                            size="sm"
                            variant={app.status === 'reviewed' ? 'default' : 'outline'}
                            onClick={() => handleStatusUpdate(app.id, 'reviewed')}
                            disabled={updatingId === app.id}
                            className={app.status === 'reviewed'
                              ? 'bg-yellow-500 hover:bg-yellow-600 text-zinc-900'
                              : 'bg-transparent border-zinc-600 text-gray-300 hover:bg-zinc-800'
                            }
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Reviewed
                          </Button>
                          <Button
                            size="sm"
                            variant={app.status === 'contacted' ? 'default' : 'outline'}
                            onClick={() => handleStatusUpdate(app.id, 'contacted')}
                            disabled={updatingId === app.id}
                            className={app.status === 'contacted'
                              ? 'bg-green-500 hover:bg-green-600 text-white'
                              : 'bg-transparent border-zinc-600 text-gray-300 hover:bg-zinc-800'
                            }
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Contacted
                          </Button>
                        </div>
                        {updatingId === app.id && (
                          <Loader2 className="w-4 h-4 text-teal-400 animate-spin" />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
